import { schemas } from 'api/__GENERATED';
import { errorWrapper } from 'utility/error';
import { z } from 'zod';

import { apiClient } from './api';
import { toFileDomain } from './transformers/toFileDomain';

// Drops to raw axios so the `x-pagination` response header is reachable;
// the Zodios alias would unwrap straight to the body.
async function getFiles(args: {
  tenantId: string;
  pageNumber: number;
  pageSize: number;
}) {
  const { tenantId, pageNumber, pageSize } = args;

  const apiCall = apiClient.axios
    .get(`/v1/tenants/${tenantId}/files`, {
      params: { PageNumber: pageNumber, PageSize: pageSize },
    })
    .then((response) => ({
      ...response,
      data: schemas.FileImportSummaryResponseApiResult.parse(response.data),
    }));

  return await errorWrapper({
    apiCall,
    error: {
      message: '',
      severity: 'warning',
      title: 'Failed to load files',
    },
    onSuccess: (data, response) => {
      const { fileDomain } = toFileDomain({ dtos: data });

      const pagination = parsePaginationHeader(
        response?.headers['x-pagination'],
      );
      return { fileDomain, pagination };
    },
  });
}

// `x-pagination` ships as a JSON-encoded string on the response header.
const paginationHeaderSchema = z.object({
  CurrentPage: z.number(),
  TotalPages: z.number(),
});

function parsePaginationHeader(raw: unknown): System.Pagination {
  if (typeof raw !== 'string') return { page: 1, totalPages: 1 };

  try {
    const parsed = paginationHeaderSchema.safeParse(JSON.parse(raw));
    if (!parsed.success) return { page: 1, totalPages: 1 };
    return {
      page: parsed.data.CurrentPage,
      totalPages: parsed.data.TotalPages,
    };
  } catch {
    return { page: 1, totalPages: 1 };
  }
}

export { getFiles };
